import { useCallback, useMemo } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { DataTablePaginationProps } from './types'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const
const MAX_VISIBLE_PAGES = 7

// ---------------------------------------------------------------------------
// Helpers (pure functions)
// ---------------------------------------------------------------------------

function computePageRange(
  pageIndex: number,
  pageSize: number,
  totalRows: number
): { start: number; end: number } {
  if (totalRows === 0) {
    return { start: 0, end: 0 }
  }
  const start = pageIndex * pageSize + 1
  const end = Math.min((pageIndex + 1) * pageSize, totalRows)
  return { start, end }
}

function computeVisiblePages(
  currentPage: number,
  pageCount: number
): (number | 'ellipsis')[] {
  if (pageCount <= 1) {
    return []
  }

  if (pageCount <= MAX_VISIBLE_PAGES) {
    return Array.from({ length: pageCount }, (_, i) => i + 1)
  }

  const pages: (number | 'ellipsis')[] = []

  // Always show first page
  pages.push(1)

  if (currentPage > 3) {
    pages.push('ellipsis')
  }

  // Pages around current
  const rangeStart = Math.max(2, currentPage - 1)
  const rangeEnd = Math.min(pageCount - 1, currentPage + 1)

  for (let i = rangeStart; i <= rangeEnd; i++) {
    pages.push(i)
  }

  if (currentPage < pageCount - 2) {
    pages.push('ellipsis')
  }

  // Always show last page
  pages.push(pageCount)

  return pages
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DataTablePagination({
  table,
  totalRows = 0,
}: DataTablePaginationProps) {
  const pageIndex = table.getState().pagination.pageIndex
  const pageSize = table.getState().pagination.pageSize
  const pageCount = table.getPageCount()
  const canPreviousPage = table.getCanPreviousPage()
  const canNextPage = table.getCanNextPage()

  const { start, end } = useMemo(
    () => computePageRange(pageIndex, pageSize, totalRows),
    [pageIndex, pageSize, totalRows]
  )

  const visiblePages = useMemo(
    () => computeVisiblePages(pageIndex + 1, pageCount),
    [pageIndex, pageCount]
  )

  const handlePreviousPage = useCallback(() => {
    table.previousPage()
  }, [table])

  const handleNextPage = useCallback(() => {
    table.nextPage()
  }, [table])

  const handlePageClick = useCallback(
    (page: number) => {
      table.setPageIndex(page - 1)
    },
    [table]
  )

  const handlePageSizeChange = useCallback(
    (value: string) => {
      const newSize = Number(value)
      table.setPageSize(newSize)
    },
    [table]
  )

  const currentPage = pageIndex + 1

  return (
    <div
      className="flex items-center justify-between px-4 py-3 border-t border-v11-border"
      data-testid="data-table-pagination"
    >
      {/* Left: Info text removed - total count was inconsistent with filtered results */}
      <div />

      {/* Center: Page navigation */}
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={handlePreviousPage}
          disabled={!canPreviousPage}
          aria-label="Previous page"
          className="text-v11-text-primary border-v11-border hover:bg-v11-surface-hover disabled:opacity-50"
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>

        {visiblePages.map((page, idx) =>
          page === 'ellipsis' ? (
            <span
              key={`ellipsis-${idx}`}
              className="flex h-9 w-9 items-center justify-center text-sm text-v11-text-muted"
              data-testid="pagination-ellipsis"
            >
              ...
            </span>
          ) : (
            <Button
              key={page}
              variant={page === currentPage ? 'default' : 'outline'}
              size="sm"
              className={cn(
                'min-w-[36px]',
                page === currentPage
                  ? 'pointer-events-none bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-v11-surface text-v11-text-primary border-v11-border hover:bg-v11-surface-hover hover:text-v11-text-primary'
              )}
              onClick={() => handlePageClick(page)}
              aria-label={`Page ${page}`}
              aria-current={page === currentPage ? 'page' : undefined}
            >
              {page}
            </Button>
          )
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={handleNextPage}
          disabled={!canNextPage}
          aria-label="Next page"
          className="text-v11-text-primary border-v11-border hover:bg-v11-surface-hover disabled:opacity-50"
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Right: Page size selector */}
      <select
        data-testid="page-size-select"
        aria-label="Rows per page"
        value={String(pageSize)}
        onChange={(e) => handlePageSizeChange(e.target.value)}
        className="h-9 w-[70px] rounded-md border border-v11-border bg-v11-surface text-v11-text-primary text-sm px-2 hover:bg-v11-surface-hover transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {PAGE_SIZE_OPTIONS.map((size) => (
          <option key={size} value={String(size)}>
            {size}
          </option>
        ))}
      </select>
    </div>
  )
}
